import React, { useCallback, useRef, useState } from "react";

import Popover from "@material-ui/core/Popover";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";

import { BottomSheet } from "shared/ui/BottomSheet";
import { CheckIcon, ChevronDownIcon } from "shared/ui/icons";

import {
    ClearOption,
    Option,
    OptionLabel,
    Options,
    Panel,
    PopoverPaper,
    Trigger,
    TriggerLabel,
} from "./styles";

export interface SelectOption {
    value: string | number;
    label: string;
}

interface SelectBaseProps {
    options: SelectOption[];
    placeholder?: string;
    // Поле блокируем на время запроса, который его же и меняет (автосохранение).
    disabled?: boolean;
    id?: string;
    className?: string;
    // Заголовок нижнего листа на узком вьюпорте.
    title?: string;
}

interface SingleSelectProps extends SelectBaseProps {
    multiple?: false;
    value: string | number | null | undefined;
    onChange: (value: string) => void;
}

interface MultipleSelectProps extends SelectBaseProps {
    multiple: true;
    value: (string | number)[];
    onChange: (value: string[]) => void;
    /*
    * Подпись пустого выбора. В фильтрах пустой набор означает «не ограничиваем», и подписать
    * его надо «Любой»/«Все», а не «Не выбрано»: это не пропуск, а осмысленное состояние.
    * */
    emptyLabel?: string;
}

type SelectProps = SingleSelectProps | MultipleSelectProps;

const DEFAULT_PLACEHOLDER = "Не выбрано";

const isMultipleSelect = (props: SelectProps): props is MultipleSelectProps =>
    props.multiple === true;

interface SelectFieldProps extends SelectBaseProps {
    multiple: boolean;
    selected: string[];
    emptyLabel: string;
    onPick: (value: string) => void;
    // Множественный режим: сброс в «не ограничиваем». В одиночном сбрасывать нечем — там
    // всегда выбрано ровно одно значение.
    onClear: (() => void) | null;
}

/*
* Вся раскладка и поведение селекта: на десктопе поповер у поля, на узком вьюпорте нижний лист —
* как у SearchableSelect и ActionMenu. Выбранное помечается галочкой, а не чекбоксом: строка
* остаётся одной кнопкой, без вложенных интерактивных элементов.
*
* Оба режима рендерятся здесь, а публичный Select только приводит к этим пропсам свои: разбирать
* объединение прямо в разметке не выходит — TS 3.9 не сужает его через ранний return.
* */
const SelectField = ({
    options,
    placeholder = DEFAULT_PLACEHOLDER,
    disabled,
    id,
    className,
    title,
    multiple,
    selected,
    emptyLabel,
    onPick,
    onClear,
}: SelectFieldProps) => {
    const theme = useTheme();
    const isWide = useMediaQuery(theme.breakpoints.up("sm"));
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState<boolean>(false);

    const closePanel = useCallback(() => setOpen(false), []);

    const isSelected = (option: SelectOption) => selected.indexOf(String(option.value)) >= 0;

    const pick = (option: SelectOption) => {
        onPick(String(option.value));

        // Множественный режим панель не закрывает — обычно отмечают несколько значений подряд.
        if (!multiple) closePanel();
    };

    const selectedLabels = options.filter(isSelected).map((option) => option.label);

    const isEmpty = selectedLabels.length === 0;

    const triggerLabel = (() => {
        if (isEmpty) return multiple ? emptyLabel : placeholder;
        if (selectedLabels.length === 1) return selectedLabels[0];

        return `${selectedLabels[0]} и ещё ${selectedLabels.length - 1}`;
    })();

    const renderPanel = (inSheet: boolean) => (
        <Panel $inSheet={inSheet}>
            <Options $inSheet={inSheet} role="listbox" aria-multiselectable={multiple}>
                {onClear !== null && !isEmpty && (
                    <ClearOption
                        type="button"
                        $selected={false}
                        $inSheet={inSheet}
                        onClick={onClear}
                    >
                        <OptionLabel>{emptyLabel}</OptionLabel>
                    </ClearOption>
                )}

                {options.map((option) => (
                    <Option
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={isSelected(option)}
                        $selected={isSelected(option)}
                        $inSheet={inSheet}
                        onClick={() => pick(option)}
                    >
                        <OptionLabel>{option.label}</OptionLabel>
                        {isSelected(option) && <CheckIcon />}
                    </Option>
                ))}
            </Options>
        </Panel>
    );

    return (
        <>
            <Trigger
                id={id}
                className={className}
                ref={triggerRef}
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                $placeholder={isEmpty}
                onClick={() => setOpen(true)}
            >
                <TriggerLabel>{triggerLabel}</TriggerLabel>
                <ChevronDownIcon />
            </Trigger>

            {isWide ? (
                <Popover
                    open={open}
                    anchorEl={triggerRef.current}
                    onClose={closePanel}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    // Селект стоит и внутри диалогов: у Popover тот же z-index, что у Dialog,
                    // и порядок отрисовки решался бы порядком в DOM. Поднимаем явно.
                    style={{ zIndex: theme.zIndex.modal + 1 }}
                    PaperProps={{
                        style: {
                            ...PopoverPaper,
                            width: triggerRef.current ? triggerRef.current.offsetWidth : undefined,
                        },
                    }}
                >
                    {renderPanel(false)}
                </Popover>
            ) : (
                <BottomSheet open={open} title={title} onClose={closePanel}>
                    {renderPanel(true)}
                </BottomSheet>
            )}
        </>
    );
};

/*
* Селект кита в двух режимах: одиночный и множественный.
*
* ⚠️ Значения наружу и внутрь ходят строками, как у нативного select, — приводит их к своему
* типу вызывающий (`Number(value)`, `values as OrderStatusDto[]`).
* */
export function Select(props: SelectProps) {
    const { options, placeholder, disabled, id, className, title } = props;

    if (isMultipleSelect(props)) {
        const selected = props.value.map(String);
        const onChange = props.onChange;

        return (
            <SelectField
                options={options}
                placeholder={placeholder}
                disabled={disabled}
                id={id}
                className={className}
                title={title}
                multiple
                selected={selected}
                emptyLabel={props.emptyLabel || placeholder || DEFAULT_PLACEHOLDER}
                onPick={(value) =>
                    onChange(
                        selected.indexOf(value) >= 0
                            ? selected.filter((item) => item !== value)
                            : [...selected, value]
                    )
                }
                onClear={() => onChange([])}
            />
        );
    }

    return (
        <SelectField
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            id={id}
            className={className}
            title={title}
            multiple={false}
            selected={props.value == null ? [] : [String(props.value)]}
            emptyLabel={placeholder || DEFAULT_PLACEHOLDER}
            onPick={props.onChange}
            onClear={null}
        />
    );
}
